import React, { useCallback, useEffect } from 'react';
import { CardViewContentBlockProps } from './types';
import { Box, Typography } from '@mui/material';
import LibraryCategoryCard from '../../../../common/library-category-card/LibraryCategoryCard';
import { useTranslation } from 'react-i18next';
import TerminationIcon from '../../../../../assets/svg/termination.svg?react';
import PolicyIcon from '../../../../../assets/svg/policy.svg?react';
import ProposalIcon from '../../../../../assets/svg/proposal.svg?react';
import ProjectMaterialsIcon from '../../../../../assets/svg/project-materials.svg?react';
import InvoicingIcon from '../../../../../assets/svg/invoicing.svg?react';
import { TAGS_LABELS } from '../../../../common/constants/tags-labels';
import ReceiptIcon from '../../../../../assets/svg/receipt-icon.svg?react';
import { Helpers } from '../../../../common/helpers/helpers';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';

export const SuppliersCategoryContent = ({ templates, setWorkflow }: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const [searchParam] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workflowItem = searchParam.get('workflow');
  const company = useAppSelector(selectAllCompanies)[0];
  const templatesFilteredByCategory = [...templates].filter(
    (template) =>
      'dl_workflows' in template && 'is_free' in template && template.category === 'Suppliers'
  );
  const getTemplatesCount = (workflow: string) => {
    return templatesFilteredByCategory.filter((el) => el.dl_workflows.includes(workflow)).length;
  };
  const getFreeTemplatesCount = (workflow: string) => {
    return templatesFilteredByCategory.filter(
      (el) => el.dl_workflows.includes(workflow) && el.is_free
    ).length;
  };
  const getTemplatesCountByWorkflow = (workflow: string) => {
    return templatesFilteredByCategory.filter((el) => el.dl_workflows.includes(workflow)).length;
  };
  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.supplier.agreement'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.supplier.proposalOrQuote'):
        return <ProposalIcon />;
      case t('tagsLabels.documentWorkflow.supplier.projectMaterials'):
        return <ProjectMaterialsIcon />;
      case t('tagsLabels.documentWorkflow.supplier.invoicing'):
        return <InvoicingIcon />;
      case t('tagsLabels.documentWorkflow.supplier.termination'):
        return <TerminationIcon />;
      case t('tagsLabels.documentWorkflow.supplier.receipts'):
        return <ReceiptIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.supplier.agreement'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.supplier.receipts'):
        return '2. ';
    }
  };

  const handleWorkflowChange = useCallback(() => {
    if (workflowItem) {
      setWorkflow(decodeURI(workflowItem));
      navigate(location.pathname);
    }
  }, [workflowItem, setWorkflow, navigate, location.pathname]);

  useEffect(() => {
    handleWorkflowChange();
  }, [handleWorkflowChange]);

  return (
    <>
      <Typography
        variant='h3'
        component='h3'
        sx={{ marginBottom: '16px', fontSize: '15px', lineHeight: '24px' }}
      >
        {t('headers.workflows')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
        }}
      >
        {TAGS_LABELS.documentWorkflow.supplier.map((el) =>
          getTemplatesCountByWorkflow(t(el)) > 0 ? (
            <LibraryCategoryCard
              key={el}
              title={Helpers.capitalizeTitle(getWorkflowNumber(t(el)) + t(el))}
              description={
                getTemplatesCount(t(el)) +
                (getTemplatesCount(t(el)) > 1 ? ' templates' : ' template')
              }
              hasFreeLabel={
                getFreeTemplatesCount(t(el)) > 0 &&
                !company?.stripe_subscription &&
                !company?.stripe_product_id
              }
              onClick={() => setWorkflow(t(el))}
              icon={getIcon(t(el))}
            />
          ) : null
        )}
      </Box>
    </>
  );
};
